import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { connect } from "react-redux";
import ReCAPTCHA from "react-google-recaptcha";
import { createSavedSearch } from "../../actions/savedSearch";
import { followCitySignup } from "../../util/adminApi";
import { RECAPTCHA_SITEKEY } from "../../config";

const SaveSearchModal = ({
  cityName,
  state: stateName,
  lat,
  lng,
  keywords,
  cuisine,
  radius,
  zoom,
  onClose,
  // Redux
  token,
  minLeafCount,
  verificationTier,
}) => {
  const isAuthenticated = !!token;

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [notifyEnabled, setNotifyEnabled] = useState(false);
  const [notifyRadius, setNotifyRadius] = useState(10);
  const [isPublic, setIsPublic] = useState(false);

  // Unauth form state
  const [email, setEmail] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState("");

  // Shared state
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [savedSlug, setSavedSlug] = useState(null);

  // Pre-fill name from city + keywords
  useEffect(function prefillNameFromCityAndKeywords() {
    const displayCity = stateName
      ? `${cityName}, ${stateName}`
      : cityName || "this area";
    if (keywords) {
      setName(`Vegan ${keywords} near ${displayCity}`);
    } else {
      setName(`Vegan options near ${displayCity}`);
    }
  }, [cityName, stateName, keywords]);

  const displayName = stateName ? `${cityName}, ${stateName}` : cityName;

  // === Authenticated submit ===
  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) {
      setError("Please enter a name for your list");
      return;
    }
    setSubmitting(true);
    try {
      const searchParams = { lat, lng };
      if (keywords) searchParams.keywords = keywords;
      if (cuisine && cuisine.length > 0) searchParams.cuisine = cuisine;
      if (radius) searchParams.radius = radius;
      if (zoom) searchParams.zoom = zoom;
      if (minLeafCount) searchParams.minLeafCount = minLeafCount;
      if (verificationTier) searchParams.verificationTier = verificationTier;

      const result = await createSavedSearch(
        {
          name: name.trim(),
          description: description.trim() || undefined,
          searchParams,
          visibility: isPublic ? "public" : "private",
          notifications: {
            enabled: notifyEnabled,
            radiusMiles: notifyRadius,
          },
        },
        token
      );
      setSuccess(true);
      setSavedSlug(result.slug);
    } catch (err) {
      const errMsg =
        err.response?.data?.errors?.[0]?.msg || "Something went wrong.";
      setError(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // === Unauthenticated submit (follow city) ===
  const handleUnauthSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!recaptchaToken) {
      setError("Please complete the reCAPTCHA");
      return;
    }
    setSubmitting(true);
    try {
      const resp = await followCitySignup({
        email,
        location: { lat, lng },
        city: cityName,
        recaptchaToken,
        preferences: {
          radiusMiles: notifyRadius,
          cityName,
          state: stateName,
          lat,
          lng,
          ...(keywords && { keywords }),
          ...(cuisine && cuisine.length > 0 && { cuisine }),
        },
      });
      if (resp?.response?.status === 400) {
        setError(resp.response.data.errors.map((obj) => obj.msg).join(", "));
      } else if (resp?.response?.status >= 500) {
        setError("Something went wrong. Please try again.");
      } else {
        setSuccess(true);
        setTimeout(() => onClose(), 2500);
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button
          className="modal-close"
          onClick={onClose}
          aria-label="Close modal"
        >
          &times;
        </button>

        {success ? (
          <>
            <i
              className="fas fa-check-circle modal-icon"
              style={{ color: "var(--color-primary)" }}
            />
            <h3 className="modal-title">
              {isAuthenticated ? "List saved!" : `You're following ${displayName}!`}
            </h3>
            <p className="modal-message">
              {isAuthenticated
                ? isPublic
                  ? "Your list is now public and shareable."
                  : "Your list has been saved to your profile."
                : "We'll let you know when new vegan restaurants are added nearby."}
            </p>
            {isAuthenticated && savedSlug && isPublic && (
              <div style={{ marginTop: "12px" }}>
                <label style={{ fontSize: "0.85em", color: "#666" }}>
                  Shareable link:
                </label>
                <input
                  type="text"
                  readOnly
                  className="text-form"
                  value={`${window.location.origin}/lists/${savedSlug}`}
                  onClick={(e) => {
                    e.target.select();
                    navigator.clipboard?.writeText(e.target.value);
                  }}
                  style={{ marginTop: "4px" }}
                />
              </div>
            )}
          </>
        ) : isAuthenticated ? (
          <>
            <i
              className="fas fa-bookmark modal-icon"
              style={{ color: "var(--color-primary)" }}
            />
            <h3 className="modal-title">Save This Search</h3>
            <p className="modal-message">
              Save your current search to access it later from your profile.
            </p>
            <form onSubmit={handleAuthSubmit} className="follow-city-form">
              <div className="follow-city-field">
                <label htmlFor="save-name">Name</label>
                <input
                  id="save-name"
                  type="text"
                  className="text-form"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Best Vegan Tacos in Portland"
                  required
                />
              </div>

              <div className="follow-city-field">
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={notifyEnabled}
                    onChange={(e) => setNotifyEnabled(e.target.checked)}
                  />
                  Notify me of new restaurants
                </label>
              </div>

              {notifyEnabled && (
                <div className="follow-city-field">
                  <label htmlFor="save-radius">Notify me within</label>
                  <select
                    id="save-radius"
                    className="text-form"
                    value={notifyRadius}
                    onChange={(e) => setNotifyRadius(Number(e.target.value))}
                  >
                    <option value={5}>5 miles</option>
                    <option value={10}>10 miles</option>
                    <option value={25}>25 miles</option>
                  </select>
                </div>
              )}

              <div className="follow-city-field">
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isPublic}
                    onChange={(e) => setIsPublic(e.target.checked)}
                  />
                  Make this list public (shareable link)
                </label>
              </div>

              {isPublic && (
                <div className="follow-city-field">
                  <label htmlFor="save-desc">Description (optional)</label>
                  <textarea
                    id="save-desc"
                    className="text-form"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="A brief description for visitors"
                    rows={2}
                    style={{ resize: "vertical" }}
                  />
                </div>
              )}

              {error && <p className="follow-city-error">{error}</p>}
              <button
                type="submit"
                className="btn-primary"
                disabled={submitting}
              >
                {submitting ? "Saving..." : "Save Search"}
              </button>
            </form>
          </>
        ) : (
          <>
            <i
              className="fas fa-bell modal-icon"
              style={{ color: "var(--color-primary)" }}
            />
            <h3 className="modal-title">Follow {displayName}</h3>
            <p className="modal-message">
              Get notified when new vegan restaurants are added in this area.
            </p>
            <form onSubmit={handleUnauthSubmit} className="follow-city-form">
              <div className="follow-city-field">
                <label htmlFor="follow-email">Email</label>
                <input
                  id="follow-email"
                  type="email"
                  className="text-form"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>
              <div className="follow-city-field">
                <label htmlFor="follow-radius">Notify me within</label>
                <select
                  id="follow-radius"
                  className="text-form"
                  value={notifyRadius}
                  onChange={(e) => setNotifyRadius(Number(e.target.value))}
                >
                  <option value={5}>5 miles</option>
                  <option value={10}>10 miles</option>
                  <option value={25}>25 miles</option>
                </select>
              </div>
              <div className="follow-city-recaptcha">
                <ReCAPTCHA
                  sitekey={RECAPTCHA_SITEKEY}
                  onChange={setRecaptchaToken}
                />
              </div>
              {error && <p className="follow-city-error">{error}</p>}
              <button
                type="submit"
                className="btn-primary"
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Follow This City"}
              </button>
              <p
                style={{
                  marginTop: "12px",
                  fontSize: "0.85em",
                  color: "#666",
                  textAlign: "center",
                }}
              >
                <a href="/login">Log in</a> to save, share, and manage your
                lists.
              </p>
            </form>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

const mapStateToProps = (state) => ({
  token: state.auth.token,
  minLeafCount: state.filters.minLeafCount,
  verificationTier: state.filters.verificationTier,
});

export default connect(mapStateToProps)(SaveSearchModal);
